/**
 * Admin Dashboard - Operation Manager
 * Comprehensive dashboard with analytics and overview
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Card, Box, CircularProgress, Chip, Divider } from "@mui/material";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Context
import { useAuth } from "context/authContext";

// API
import { adminAPI } from "utils/api";

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    // Properties Analytics
    totalProperties: 0,
    pendingProperties: 0,
    inspectionProperties: 0,
    inspectionCompletedProperties: 0,
    approvedProperties: 0,
    rejectedProperties: 0,
    propertiesReadyForApproval: 0,
    propertiesByType: {},
    propertiesByCity: {},
    propertiesByStatus: {},

    // Property Owners Analytics
    totalPropertyOwners: 0,
    activePropertyOwners: 0,
    pendingPropertyOwners: 0,

    // Operations Analytics
    totalInspectors: 0,
    activeInspections: 0,
    completedInspections: 0,
    pendingInspections: 0,
    inspectionsThisWeek: 0,
    inspectionsThisMonth: 0,

    // System Analytics
    averageInspectionTime: 0,
    propertiesWithoutInspector: 0,
    inspectionCompletionRate: 0,
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [properties, propertyOwners, inspectors, operationsTeam] = await Promise.all([
        adminAPI.getAllProperties().catch((err) => {
          console.error("Error fetching properties:", err);
          return { properties: [] };
        }),
        adminAPI.getAllPropertyOwners().catch((err) => {
          console.error("Error fetching property owners:", err);
          return { data: [], owners: [] };
        }),
        adminAPI.getAvailableInspectors().catch((err) => {
          console.error("Error fetching inspectors:", err);
          return { inspectors: [] };
        }),
        adminAPI.getOperationsTeam().catch((err) => {
          console.error("Error fetching operations team:", err);
          return { inspectors: [], agents: [] };
        }),
      ]);

      const propertiesList = properties.properties || properties.data || [];

      // Handle different response formats for property owners
      // Backend returns: { message, count, owners }
      // API utility transforms it to: { ...response, data: response.owners || [] }
      let ownersList = [];
      if (Array.isArray(propertyOwners)) {
        ownersList = propertyOwners;
      } else if (propertyOwners?.data && Array.isArray(propertyOwners.data)) {
        ownersList = propertyOwners.data;
      } else if (propertyOwners?.owners && Array.isArray(propertyOwners.owners)) {
        ownersList = propertyOwners.owners;
      } else if (propertyOwners && typeof propertyOwners === "object") {
        // Try to extract owners from any property
        const possibleOwners =
          propertyOwners.owners || propertyOwners.data || propertyOwners.propertyOwners;
        if (Array.isArray(possibleOwners)) {
          ownersList = possibleOwners;
        }
      }

      // Debug logging (remove in production)
      if (ownersList.length === 0 && propertyOwners) {
        console.log("Property Owners Response Structure:", {
          hasData: !!propertyOwners.data,
          hasOwners: !!propertyOwners.owners,
          keys: Object.keys(propertyOwners),
          fullResponse: propertyOwners,
        });
      }

      const inspectorsList = inspectors.inspectors || inspectors.data || [];
      // Get operations team members (inspectors + agents) for analytics
      const operationsTeamList = operationsTeam.inspectors || operationsTeam.data?.inspectors || [];
      const agentsList = operationsTeam.agents || operationsTeam.data?.agents || [];
      const usersList = [...operationsTeamList, ...agentsList];

      // ===== PROPERTIES ANALYTICS =====
      const totalProperties = propertiesList.length;
      const pendingProperties = propertiesList.filter((p) => p.status === "Pending").length;
      const inspectionProperties = propertiesList.filter((p) => p.status === "Inspection").length;
      const inspectionCompletedProperties = propertiesList.filter(
        (p) => p.status === "Inspection_Completed"
      ).length;
      const approvedProperties = propertiesList.filter((p) => p.status === "Approved").length;
      const rejectedProperties = propertiesList.filter((p) => p.status === "Rejected").length;
      const propertiesReadyForApproval = inspectionCompletedProperties;

      // Properties by type
      const propertiesByType = {};
      propertiesList.forEach((p) => {
        const type = p.propertyType || "Unknown";
        propertiesByType[type] = (propertiesByType[type] || 0) + 1;
      });

      // Properties by city
      const propertiesByCity = {};
      propertiesList.forEach((p) => {
        const city = p.city || "Unknown";
        propertiesByCity[city] = (propertiesByCity[city] || 0) + 1;
      });

      // Properties by status
      const propertiesByStatus = {};
      propertiesList.forEach((p) => {
        const status = p.status || "Unknown";
        propertiesByStatus[status] = (propertiesByStatus[status] || 0) + 1;
      });

      // ===== PROPERTY OWNERS ANALYTICS =====
      const totalPropertyOwners = Array.isArray(ownersList) ? ownersList.length : 0;
      const activePropertyOwners = Array.isArray(ownersList)
        ? ownersList.filter((o) => o.status === "Active").length
        : 0;
      const pendingPropertyOwners = Array.isArray(ownersList)
        ? ownersList.filter((o) => o.status === "Pending").length
        : 0;

      // ===== OPERATIONS ANALYTICS =====
      const totalInspectors = inspectorsList.length;
      const activeInspections = inspectionProperties;
      const completedInspections = inspectionCompletedProperties;
      const pendingInspections = pendingProperties;

      // Calculate inspections this week and month
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const inspectionsThisWeek = propertiesList.filter((p) => {
        if (!p.inspectionDate) return false;
        const inspectionDate = new Date(p.inspectionDate);
        return inspectionDate >= weekAgo && inspectionDate <= now;
      }).length;

      const inspectionsThisMonth = propertiesList.filter((p) => {
        if (!p.inspectionDate) return false;
        const inspectionDate = new Date(p.inspectionDate);
        return inspectionDate >= monthAgo && inspectionDate <= now;
      }).length;

      // Properties without inspector
      const propertiesWithoutInspector = propertiesList.filter(
        (p) => !p.assignedInspector && p.status === "Pending"
      ).length;

      // Inspection completion rate
      const totalInspections = activeInspections + completedInspections;
      const inspectionCompletionRate =
        totalInspections > 0 ? Math.round((completedInspections / totalInspections) * 100) : 0;

      setStats({
        totalProperties,
        pendingProperties,
        inspectionProperties,
        inspectionCompletedProperties,
        approvedProperties,
        rejectedProperties,
        propertiesReadyForApproval,
        propertiesByType,
        propertiesByCity,
        propertiesByStatus,
        totalPropertyOwners,
        activePropertyOwners,
        pendingPropertyOwners,
        totalInspectors,
        activeInspections,
        completedInspections,
        pendingInspections,
        inspectionsThisWeek,
        inspectionsThisMonth,
        propertiesWithoutInspector,
        inspectionCompletionRate,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (part, total) => {
    if (total > 0) {
      return Math.round((part / total) * 100);
    }
    return 0;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={3}
        sx={{
          height: "calc(100vh - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        {/* Header */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Operation Manager Dashboard
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Welcome back, {user?.fullName || "Admin"}! Here's an overview of your operations.
          </MDTypography>
        </MDBox>

        {/* Key Statistics Cards */}
        <Grid container spacing={3} mb={3} sx={{ "& .MuiGrid-item": { display: "flex" } }}>
          {/* Properties Statistics */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/properties")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="info"
                icon="home"
                title="Total Properties"
                count={stats.totalProperties}
                percentage={{
                  color: "success",
                  amount: `${calculatePercentage(
                    stats.approvedProperties,
                    stats.totalProperties
                  )}%`,
                  label: "approved",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/properties?status=Pending")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="error"
                icon="schedule"
                title="Pending Review"
                count={stats.pendingProperties}
                percentage={{
                  color: "error",
                  amount: `${stats.propertiesWithoutInspector}`,
                  label: "need inspector",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/properties?status=Inspection")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="warning"
                icon="search"
                title="Under Inspection"
                count={stats.inspectionProperties}
                percentage={{
                  color: "warning",
                  amount: `${stats.inspectionsThisWeek}`,
                  label: "this week",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/properties?status=Inspection_Completed")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="success"
                icon="task_alt"
                title="Ready for Approval"
                count={stats.propertiesReadyForApproval}
                percentage={{
                  color: "success",
                  amount: `${stats.inspectionCompletionRate}%`,
                  label: "completion rate",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Secondary Statistics */}
        <Grid container spacing={3} mb={3} sx={{ "& .MuiGrid-item": { display: "flex" } }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/property-owners")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="primary"
                icon="people"
                title="Property Owners"
                count={stats.totalPropertyOwners}
                percentage={{
                  color: "success",
                  amount: `${stats.activePropertyOwners}`,
                  label: "active",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/operations-team")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="secondary"
                icon="assignment_ind"
                title="Operations Team"
                count={stats.totalInspectors}
                percentage={{
                  color: "info",
                  amount: `${stats.activeInspections}`,
                  label: "active inspections",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/inspections")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="info"
                icon="event"
                title="This Month"
                count={stats.inspectionsThisMonth}
                percentage={{
                  color: "success",
                  amount: `${stats.completedInspections}`,
                  label: "completed",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              onClick={() => navigate("/admin/properties?status=Rejected")}
              sx={{ cursor: "pointer", width: "100%", height: "100%" }}
            >
              <ComplexStatisticsCard
                color="dark"
                icon="cancel"
                title="Rejected"
                count={stats.rejectedProperties}
                percentage={{
                  color: "error",
                  amount: `${calculatePercentage(
                    stats.rejectedProperties,
                    stats.totalProperties
                  )}%`,
                  label: "of total",
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "180px",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Detailed Analytics Cards */}
        <Grid container spacing={3}>
          {/* Properties by Type */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card
              sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: "300px" }}
            >
              <MDBox p={3} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>category</Icon>
                  Properties by Type
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox>
                  {Object.keys(stats.propertiesByType).length > 0 ? (
                    Object.entries(stats.propertiesByType)
                      .sort(([, a], [, b]) => b - a)
                      .map(([type, count]) => (
                        <MDBox
                          key={type}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1.5}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MDTypography>
                          <Chip label={count} color="info" size="small" sx={{ fontWeight: 600 }} />
                        </MDBox>
                      ))
                  ) : (
                    <MDTypography variant="body2" color="text.secondary">
                      No properties data available
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Properties by City */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card
              sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: "300px" }}
            >
              <MDBox p={3} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>location_city</Icon>
                  Properties by City
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox>
                  {Object.keys(stats.propertiesByCity).length > 0 ? (
                    Object.entries(stats.propertiesByCity)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 10)
                      .map(([city, count]) => (
                        <MDBox
                          key={city}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1.5}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            {city}
                          </MDTypography>
                          <Chip
                            label={count}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </MDBox>
                      ))
                  ) : (
                    <MDTypography variant="body2" color="text.secondary">
                      No city data available
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Properties by Status */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card
              sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: "300px" }}
            >
              <MDBox p={3} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>assessment</Icon>
                  Properties by Status
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox>
                  {Object.keys(stats.propertiesByStatus).length > 0 ? (
                    Object.entries(stats.propertiesByStatus)
                      .sort(([, a], [, b]) => b - a)
                      .map(([status, count]) => {
                        const getStatusColor = (status) => {
                          switch (status) {
                            case "Approved":
                              return "success";
                            case "Pending":
                              return "error";
                            case "Inspection":
                            case "Inspection_Completed":
                              return "info";
                            case "Rejected":
                              return "dark";
                            default:
                              return "secondary";
                          }
                        };
                        return (
                          <MDBox
                            key={status}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1.5}
                          >
                            <MDTypography variant="body2" fontWeight="medium">
                              {status.replace("_", " ")}
                            </MDTypography>
                            <Chip
                              label={count}
                              color={getStatusColor(status)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </MDBox>
                        );
                      })
                  ) : (
                    <MDTypography variant="body2" color="text.secondary">
                      No status data available
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card
              sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: "300px" }}
            >
              <MDBox p={3} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>flash_on</Icon>
                  Quick Actions
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox>
                  <MDBox mb={2}>
                    <MDTypography variant="body2" color="text.secondary" mb={1}>
                      Pending Actions
                    </MDTypography>
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                      {stats.pendingProperties > 0 && (
                        <Chip
                          icon={<Icon>schedule</Icon>}
                          label={`${stats.pendingProperties} Properties Need Review`}
                          color="error"
                          size="small"
                        />
                      )}
                      {stats.propertiesWithoutInspector > 0 && (
                        <Chip
                          icon={<Icon>assignment</Icon>}
                          label={`${stats.propertiesWithoutInspector} Need Inspector`}
                          color="warning"
                          size="small"
                        />
                      )}
                      {stats.propertiesReadyForApproval > 0 && (
                        <Chip
                          icon={<Icon>check_circle</Icon>}
                          label={`${stats.propertiesReadyForApproval} Ready for Approval`}
                          color="success"
                          size="small"
                        />
                      )}
                      {stats.pendingPropertyOwners > 0 && (
                        <Chip
                          icon={<Icon>people</Icon>}
                          label={`${stats.pendingPropertyOwners} Owners Pending`}
                          color="info"
                          size="small"
                        />
                      )}
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="body2" color="text.secondary" mb={1}>
                      This Week's Activity
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {stats.inspectionsThisWeek} inspections scheduled
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {stats.inspectionsThisMonth} inspections this month
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default AdminDashboard;
